﻿import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo as restoreScroll } from '@theme/scroll-container';

const STORAGE_KEY = 'sonder:club:v1';
const VISIT_KEY = 'sonder:club:visit:v1';
const WEEK = 7 * 24 * 60 * 60 * 1000;
const GRACE = 10000;
const QUIET = 1500;

/** One invitation per visit; timestamps only, never a subscription assertion.
 * @extends Component<{dialog: HTMLDialogElement}>
 */
class SonderClub extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.teardown();
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  setup() {
    this.abort = new AbortController();
    this.dialog = this.refs.dialog;
    this.lastInteraction = 0;
    this.storageReady = this.checkStorage();
    const options = { signal: this.abort.signal };
    const activity = () => { this.lastInteraction = Date.now(); };
    for (const type of ['pointerdown', 'keydown', 'input', 'scroll']) {
      document.addEventListener(type, activity, { ...options, passive: true, capture: true });
    }
    document.addEventListener('click', (event) => this.handleLink(event), options);
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY && this.cooling()) clearTimeout(this.timer);
    }, options);
    window.addEventListener('pagehide', () => {
      clearTimeout(this.timer);
      if (this.dialog.open) this.dismiss();
    }, options);
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.plan(); }, options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-club-dismiss]')) this.dismiss();
    }, options);
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    }, options);
    this.dialog.addEventListener('keydown', (event) => this.containFocus(event), options);
    this.dialog.addEventListener('close', () => this.restore(), options);
    this.plan();
  }

  teardown() {
    clearTimeout(this.timer);
    this.abort?.abort();
    if (this.dialog?.open) {
      this.dialog.close();
      this.restore();
    }
    if (this.dialog) unlockScroll(this.dialog);
  }

  checkStorage() {
    try {
      // Both stores are required so navigation cannot create a new invitation loop.
      for (const [store, key] of [[localStorage, STORAGE_KEY], [sessionStorage, VISIT_KEY]]) {
        store.setItem(key + ':probe', '1');
        store.removeItem(key + ':probe');
      }
      return true;
    } catch { return false; }
  }

  readRecord() {
    if (!this.storageReady) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const value = JSON.parse(raw);
      if (!value || typeof value !== 'object' || ['impressionAt', 'interactionAt'].some(key =>
        value[key] !== undefined && (!Number.isFinite(value[key]) || value[key] < 0))) throw new Error('Invalid timestamp');
      return value;
    } catch {
      this.storageReady = false;
      return null;
    }
  }

  cooling() {
    const record = this.readRecord();
    if (!record) return true;
    const latest = Math.max(record.impressionAt || 0, record.interactionAt || 0);
    return latest > 0 && Date.now() - latest < WEEK;
  }

  visit() {
    const raw = sessionStorage.getItem(VISIT_KEY);
    const visit = raw ? JSON.parse(raw) : { startedAt: performance.timeOrigin, handled: false };
    if (!visit || !Number.isFinite(visit.startedAt) || typeof visit.handled !== 'boolean') throw new Error('Invalid visit');
    sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
    return visit;
  }

  finishVisit() {
    clearTimeout(this.timer);
    try {
      const visit = this.visit();
      sessionStorage.setItem(VISIT_KEY, JSON.stringify({ ...visit, handled: true }));
    } catch { this.storageReady = false; }
  }

  eligibleRoute() {
    const root = window.Shopify?.routes?.root || '/';
    const pathname = location.pathname;
    const path = root !== '/' && pathname.startsWith(root) ? '/' + pathname.slice(root.length) : pathname;
    return !/^\/(?:checkouts?|cart|account|admin|apps|challenge|password|orders|thank_you|wallets|auth)(?:\/|$)/i.test(path);
  }

  plan() {
    clearTimeout(this.timer);
    if (this.dataset.enabled !== 'true' || window.Shopify?.designMode || !this.storageReady || !this.eligibleRoute() || this.cooling()) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.plan(), { once: true, signal: this.abort.signal });
      return;
    }
    try {
      const visit = this.visit();
      if (visit.handled) return;
      this.due = visit.startedAt;
      this.deadline = this.due + GRACE;
      this.attempt();
    } catch { this.storageReady = false; }
  }

  busy() {
    if (document.visibilityState !== 'visible' || document.documentElement.hasAttribute('scroll-lock')) return true;
    if (Date.now() - this.lastInteraction < QUIET) return true;
    const active = document.activeElement;
    if (active instanceof Element && active.closest('input,textarea,select,[contenteditable="true"],a,button,summary,[role="combobox"]')) return true;
    const overlays = document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"],[popover]:popover-open,#Details-menu-drawer-container[open],header-menu [aria-expanded="true"]');
    return [...overlays].some(element => element !== this.dialog && element.checkVisibility({ visibilityProperty: true }));
  }

  attempt() {
    if (!this.storageReady || this.cooling()) return;
    try {
      if (this.visit().handled) return;
      if (Date.now() > this.deadline) { this.finishVisit(); return; }
      if (this.busy()) { this.timer = setTimeout(() => this.attempt(), 500); return; }
      this.previousFocus = document.activeElement;
      this.previousScroll = getScrollTop();
      this.dialog.showModal();
      if (!this.dialog.open) return;
      lockScroll(this.dialog);
      // Only an actual open dialog counts as an impression.
      const record = this.readRecord();
      if (record) localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...record, impressionAt: Date.now() }));
      this.finishVisit();
      this.dialog.querySelector('[data-club-dismiss]')?.focus({ preventScroll: true });
    } catch {
      this.storageReady = false;
      this.finishVisit();
      if (this.dialog.open) this.dismiss();
    }
  }

  handleLink(event) {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || (!link.hasAttribute('data-sonder-club-link') && link.href !== this.dataset.clubUrl)) return;
    if (this.storageReady) {
      try {
        const record = this.readRecord();
        if (record) localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...record, interactionAt: Date.now() }));
        this.finishVisit();
      } catch { this.storageReady = false; }
    }
    if (this.dialog.open) this.dismiss();
    // Native link navigation remains untouched. A click is not a verified subscription.
  }

  containFocus(event) {
    if (event.key !== 'Tab') return;
    const controls = [...this.dialog.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')]
      .filter(element => element.checkVisibility());
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  dismiss() {
    if (this.dialog.open) this.dialog.close();
    this.restore();
  }

  restore() {
    unlockScroll(this.dialog);
    if (this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected && this.previousFocus !== document.body) {
      this.previousFocus.focus({ preventScroll: true });
    }
    if (Number.isFinite(this.previousScroll)) restoreScroll({ top: this.previousScroll, behavior: 'instant' });
    this.previousFocus = null;
    this.previousScroll = undefined;
  }
}

if (!customElements.get('sonder-club')) customElements.define('sonder-club', SonderClub);
